import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { receiptVoucherAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './ReceiptVoucherCreate.css';

const ReceiptVoucherEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    voucherNumber: '',
    voucherDate: new Date().toISOString().split('T')[0],
    receivedFrom: '',
    amount: '',
    amountInWords: '',
    paymentMethod: '',
    chequeNumber: '',
    bank: '',
    purpose: '',
    isActive: 'true',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadReceiptVoucher();
  }, [id]);

  const loadReceiptVoucher = async () => {
    try {
      setLoadingData(true);
      const response = await receiptVoucherAPI.getById(id);
      const data = response.data;
      setFormData({
        ...data,
        voucherDate: data.voucherDate ? new Date(data.voucherDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        amount: data.amount?.toString() || '',
        isActive: data.isActive ? 'true' : 'false',
      });
    } catch (err) {
      setErrorMessage(t('receiptVoucher.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.voucherNumber.trim()) {
      newErrors.voucherNumber = t('receiptVoucher.edit.errors.voucherNumberRequired');
    }
    if (!formData.voucherDate) {
      newErrors.voucherDate = t('receiptVoucher.edit.errors.voucherDateRequired');
    }
    if (!formData.receivedFrom.trim()) {
      newErrors.receivedFrom = t('receiptVoucher.edit.errors.receivedFromRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('receiptVoucher.edit.errors.amountRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        isActive: formData.isActive === 'true',
      };
      await receiptVoucherAPI.update(id, submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/receipt-voucher');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(err.response?.data?.message || t('receiptVoucher.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-hashtag"></i> {t('receiptVoucher.edit.fields.voucherNumber')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-hashtag input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.voucherNumber ? 'is-invalid' : ''}`}
                  name="voucherNumber"
                  value={formData.voucherNumber}
                  onChange={handleChange}
                  placeholder={t('receiptVoucher.edit.fields.voucherNumber')}
                  maxLength={50}
                />
              </div>
              {errors.voucherNumber && (
                <div className="error-message">{errors.voucherNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('receiptVoucher.edit.fields.voucherDate')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.voucherDate ? 'is-invalid' : ''}`}
                  name="voucherDate"
                  value={formData.voucherDate}
                  onChange={handleChange}
                />
              </div>
              {errors.voucherDate && (
                <div className="error-message">{errors.voucherDate}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('receiptVoucher.edit.fields.receivedFrom')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.receivedFrom ? 'is-invalid' : ''}`}
                  name="receivedFrom"
                  value={formData.receivedFrom}
                  onChange={handleChange}
                  placeholder={t('receiptVoucher.edit.fields.receivedFrom')}
                  maxLength={255}
                />
              </div>
              {errors.receivedFrom && (
                <div className="error-message">{errors.receivedFrom}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('receiptVoucher.edit.fields.amount')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  className={`form-input ${errors.amount ? 'is-invalid' : ''}`}
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={t('receiptVoucher.edit.fields.amount')}
                />
              </div>
              {errors.amount && (
                <div className="error-message">{errors.amount}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-alt"></i> {t('receiptVoucher.edit.fields.amountInWords')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="amountInWords"
                  value={formData.amountInWords}
                  onChange={handleChange}
                  placeholder={t('receiptVoucher.edit.fields.amountInWords')}
                  maxLength={500}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-credit-card"></i> {t('receiptVoucher.edit.fields.paymentMethod')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-credit-card input-icon"></i>
                <select
                  className="form-input"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                >
                  <option value="">{t('common.select')}</option>
                  <option value="Cash">{t('receiptVoucher.edit.paymentMethods.cash')}</option>
                  <option value="Cheque">{t('receiptVoucher.edit.paymentMethods.cheque')}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-invoice"></i> {t('receiptVoucher.edit.fields.chequeNumber')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-invoice input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="chequeNumber"
                  value={formData.chequeNumber}
                  onChange={handleChange}
                  placeholder={t('receiptVoucher.edit.fields.chequeNumber')}
                  maxLength={100}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-university"></i> {t('receiptVoucher.edit.fields.bank')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-university input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="bank"
                  value={formData.bank}
                  onChange={handleChange}
                  placeholder={t('receiptVoucher.edit.fields.bank')}
                  maxLength={255}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-info-circle"></i> {t('receiptVoucher.edit.fields.purpose')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-info-circle input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  placeholder={t('receiptVoucher.edit.fields.purpose')}
                  maxLength={500}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('receiptVoucher.edit.fields.isActive')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <select
                  className="form-input"
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                >
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/receipt-voucher')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('receiptVoucher.edit.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('receiptVoucher.edit.messages.updateSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default ReceiptVoucherEdit;

